import { stepConfirmation } from './confirmation.js';
import { isPeopleTask, isTaskType, stepsByKey, type MapModel } from './model.js';

/**
 * The counts under a map (ADR-017 §8, rule 22). Counted by code from the
 * model on every read and never stored. Durations, costs and savings are
 * never estimated.
 */
export interface MapMetrics {
  /** Tasks people do (every task but the system's), on the main path. */
  steps_people_do: number;
  /** Of those, the ones done on paper or in a spreadsheet (manual tasks). */
  steps_on_paper: number;
  /** Tasks the system does on its own, on the main path. */
  steps_system_does: number;
  /** Work passing from one person's lane to another's on the main path. */
  handoffs: number;
  /** Task steps anywhere on the map with no confirmed evidence and no client validation. */
  unconfirmed_steps: number;
  /** Distinct pain points on the map's steps, anywhere on the map. */
  pain_points: number;
}

export interface MapHandoff {
  /** The step work leaves (a person's step or a start)… */
  from: string;
  /** …and the next person's step it reaches, in another lane. */
  to: string;
}

/**
 * The prototype's `metrics()`. Steps people do, on paper and done by the
 * system, and hand-offs, are counted on the main path: the steps reached from
 * a start along flows that are not exceptions, so exception branches are left
 * out. Unconfirmed steps and pain points are counted over the whole map, as
 * the prototype counts them.
 */
export function mapMetrics(model: MapModel): MapMetrics {
  const main = mainPath(model);
  const tasks = model.steps.filter((s) => main.has(s.key) && isTaskType(s.type));
  return {
    steps_people_do: tasks.filter((s) => isPeopleTask(s.type)).length,
    steps_on_paper: tasks.filter((s) => s.type === 'manual_task').length,
    steps_system_does: tasks.filter((s) => s.type === 'service_task').length,
    handoffs: mapHandoffs(model).length,
    unconfirmed_steps: model.steps.filter((s) => stepConfirmation(s).confirmation === 'unconfirmed')
      .length,
    pain_points: new Set(model.steps.flatMap((s) => s.pain_points.map((p) => p.pain_point_id)))
      .size,
  };
}

/** The steps reached from a start along flows that are not exceptions. */
export function mainPath(model: MapModel): Set<string> {
  const next = mainFlows(model);
  const main = new Set<string>();
  const stack = model.steps.filter((s) => s.type === 'start_event').map((s) => s.key);
  while (stack.length > 0) {
    const key = stack.pop()!;
    if (main.has(key)) continue;
    main.add(key);
    stack.push(...(next.get(key) ?? []));
  }
  return main;
}

/**
 * Hand-offs on the main path. From each person's step (or start) on it, the
 * next people's steps are found by following the flows past decisions and
 * the system's steps, stopping at an end; each one in another lane is a
 * hand-off. So automation in between is not counted as a hand-off.
 */
export function mapHandoffs(model: MapModel): MapHandoff[] {
  const steps = stepsByKey(model);
  const next = mainFlows(model);
  const main = mainPath(model);
  const order = new Map(model.steps.map((s, i) => [s.key, i]));
  const handoffs: MapHandoff[] = [];
  for (const step of model.steps) {
    if (!main.has(step.key)) continue;
    if (!isPeopleTask(step.type) && step.type !== 'start_event') continue;
    const people = new Set<string>();
    const seen = new Set<string>();
    const stack = [...(next.get(step.key) ?? [])];
    while (stack.length > 0) {
      const key = stack.pop()!;
      if (seen.has(key)) continue;
      seen.add(key);
      const s = steps.get(key)!;
      if (isPeopleTask(s.type)) people.add(key);
      else if (s.type !== 'end_event') stack.push(...(next.get(key) ?? []));
    }
    [...people]
      .filter((key) => steps.get(key)!.lane !== step.lane)
      .sort((a, b) => order.get(a)! - order.get(b)!)
      .forEach((to) => handoffs.push({ from: step.key, to }));
  }
  return handoffs;
}

/** Successors along flows that are not exceptions, between steps that exist. */
function mainFlows(model: MapModel): Map<string, string[]> {
  const keys = new Set(model.steps.map((s) => s.key));
  const next = new Map<string, string[]>();
  for (const f of model.flows) {
    if (f.is_exception || !keys.has(f.from) || !keys.has(f.to)) continue;
    next.set(f.from, [...(next.get(f.from) ?? []), f.to]);
  }
  return next;
}
